import { useMemo } from "react";
import { calculatePermille } from "../calculatePermille";
import { DrunkDrink } from "../slices/alcoholSlice";
import { User } from "../slices/userSlice";

export type PermilleChartData = { permille: number; time: number }[];

export default function usePermilleChartData({
  user,
  DrunkDrinks,
  includeCurrentPermille = false,
  includeFirstPermille = false,
  includeCustomTimestamps,
  autocut = true,
}: {
  user: User;
  DrunkDrinks: DrunkDrink[];
  includeCurrentPermille?: boolean;
  includeFirstPermille?: boolean;
  includeCustomTimestamps?: number[];
  autocut?: boolean;
}) {
  const chartData: PermilleChartData = useMemo(() => {
    let newChartData: PermilleChartData = [];

    if (DrunkDrinks.length === 0) {
      return newChartData;
    }

    DrunkDrinks.forEach((drunkDrink, index) => {
      if (index !== 0 || includeFirstPermille) {
        const permilleBeforeDrink = calculatePermille({
          DrunkDrinks: DrunkDrinks.slice(0, index),
          user,
          timestamp: drunkDrink.date - 1,
        });
        newChartData.push({
          permille: permilleBeforeDrink,
          time: drunkDrink.date - 1,
        });
      }

      const permilleAfterDrink = calculatePermille({
        DrunkDrinks: DrunkDrinks.slice(0, index + 1),
        user,
        timestamp: drunkDrink.date,
      });

      newChartData.push({
        permille: permilleAfterDrink,
        time: drunkDrink.date,
      });
    });

    if (includeCurrentPermille) {
      let timestamp = new Date().getTime();

      const currentPermille = calculatePermille({
        DrunkDrinks,
        user,
      });

      if (autocut && currentPermille === 0) {
        const sessionLength = Math.max(
          (DrunkDrinks[0]?.date ?? 0) -
            (DrunkDrinks[DrunkDrinks.length - 1]?.date ?? 0),
          60 * 60 * 1000
        );
        const sinceLastDrink =
          timestamp - DrunkDrinks[DrunkDrinks.length - 1].date;
        if (sinceLastDrink > sessionLength * 0.5) {
          timestamp =
            DrunkDrinks[DrunkDrinks.length - 1].date + sessionLength * 0.5;
        }
      }

      const CutCurrentPermille = calculatePermille({
        DrunkDrinks,
        user,
        timestamp,
      });

      newChartData.push({
        permille: CutCurrentPermille,
        time: timestamp,
      });
    }

    includeCustomTimestamps?.forEach((timestamp) => {
      if (!timestamp) return;

      const customPermille = calculatePermille({
        DrunkDrinks: DrunkDrinks.filter(
          (drunkDrink) => drunkDrink.date <= timestamp
        ),
        user,
        timestamp,
      });

      newChartData.push({
        permille: customPermille,
        time: timestamp,
      });
    });

    newChartData = newChartData.sort((a, b) => a.time - b.time);

    return newChartData;
  }, [
    DrunkDrinks,
    autocut,
    includeCurrentPermille,
    includeCustomTimestamps,
    includeFirstPermille,
    user,
  ]);

  return chartData;
}
